import React, { Fragment, useState } from "react";
import {
  Button,
  useDataProvider,
  useRefresh,
  useUnselectAll,
} from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import TableResultConfirmAdhocFees from "./TableResultConfirmAdhocFees";
import { delay } from "utils/tools";
import SaveIcon from "@material-ui/icons/Save";

interface IBulkActionProps {
  selectedIds: number[];
  basePath: string;
  filterValues: any;
  label: string;
  status?: "Pending" | "Approved" | "Rejected";
}

interface IResults {
  id: number;
  status: string;
}

const useStyles = makeStyles({
  actionColor: {
    color: "#FFF",
  },
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

const BulkActionDeleteButton = ({ selectedIds, status }: IBulkActionProps) => {
  const [open, setOpen] = useState(false);
  const [finished, setFinished] = useState(false);
  const [results, setResults] = useState<IResults[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useRefresh();
  const unselectAll = useUnselectAll();
  const provider = useDataProvider();
  const classes = useStyles();

  const selectedIdsWithStatus = selectedIds.map((id) => ({
    id,
    status: "Pending",
  }));

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (finished) {
      refresh();
    }

    setOpen(false);
  };

  const handleDelete = (selectedId: IResults, index: number) => {
    provider
      .customDelete(`subcontractorAdhocFees/${selectedId.id}/delete`, {
        data: {},
      })
      .then(() => {
        let tempInvoices = selectedIdsWithStatus;
        tempInvoices[index].status = "Success";
        setResults([...tempInvoices]);
      })
      .catch((err: any) => {
        let tempInvoices = selectedIdsWithStatus;
        tempInvoices[index].status = `Error, ${err.message}`;
        setResults([...tempInvoices]);
      });
  };

  const handleConfirm = async (selectedIdsWithStatus: IResults[]) => {
    setIsLoading(true);
    selectedIdsWithStatus.forEach((selectedId) => {
      selectedId.status = "Loading...";
    });

    for (const selectedId of selectedIdsWithStatus) {
      const index = selectedIdsWithStatus.indexOf(selectedId);

      await handleDelete(selectedId, index);
      await delay(2000);
    }

    await setFinished(true);
    await unselectAll("subcontractorAdhocFees");
    setIsLoading(false);
  };

  return (
    <Fragment>
      <Button
        label={"Delete Selected"}
        onClick={handleOpen}
        style={{ backgroundColor: "#f44336" }}
      />
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Delete Selected Adhoc Fees
        </DialogTitle>
        <DialogContent>
          {results.length > 0 ? (
            <TableResultConfirmAdhocFees records={results} />
          ) : (
            <DialogContentText color="initial">
              Are you sure you want to delete this selected adhoc fees with ID
              ({selectedIds.toString()})?
              <br/>
              {status === "Approved" &&
                "If you delete these approved adhoc fees, the records will not appear " +
                "on the subcontractor purchase invoice."}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {!finished && (
            <Button
              onClick={() => handleConfirm(selectedIdsWithStatus)}
              color="primary"
              label="Confirm"
              disabled={isLoading}
            >
              <SaveIcon />
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            label={`${finished ? "Finish" : "Back"}`}
            disabled={isLoading}
          />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

export default BulkActionDeleteButton;